<template>
  <article class="section">
    <div class="container content fill-height">
      <section v-if="$route.params.docid" id="doc">      
        <header>
          <h1 class="title">{{meta.title || $route.params.docid}}</h1>
          <div id="meta" v-if="meta.date || meta.author || meta.tags" class="subtitle">
            {{meta.date}}
            <span id="author" v-if="meta.author">&nbsp; <em>by {{meta.author}}</em></span>
            <span id="tags">
              <span v-for="tag in meta.tags">
                &nbsp;<router-link :to="'/'+dir+'?cat='+tag">
                  <b-tag class="is-primary is-small" :class="tag">{{tag}}</b-tag>
                </router-link>
              </span>
            </span>
          </div>
        </header>
        <vue-markdown v-if="source" id="body" :source="source" :breaks="false" :postrender="demoteHeadings"> </vue-markdown>
        <div id="float-clearer"></div>
      </section>
    
      <section v-else class="container content fill-height">
        <h1 class="title">{{dirName}}</h1>
        <hr>
          <b-table :data="index">
            <b-table-column label="Title" sortable field="title" v-slot="props">
              <router-link :to="'/'+dir+'/'+props.row.slug">
                <span class="title is-4"> {{props.row.title}} </span>
                <figure v-if="props.row.thumb" class="thumb">
                  <img :src="props.row.thumb"/>
                </figure>
                <p id="summary" v-html="props.row.summary"/>
              </router-link>
            </b-table-column>
            <b-table-column label="Posted" sortable field="date" v-slot="props">
              <span>{{props.row.date | formatDate}}</span>
            </b-table-column>
            <b-table-column label="Author" sortable field="author" v-slot="props">
              <span class="subtitle is-6">
                <em>{{props.row.author}}</em></span>
            </b-table-column>
            <b-table-column label="Tags" sortable field="tags" v-slot="props">
              <span v-for="t in props.row.tags">
                <router-link :to="'/'+dir+'?cat='+t">
                  <b-tag :class="t" class="tag">{{t}}</b-tag>
                </router-link>
              </span>
            </b-table-column>
          </b-table>
      </section>
    
    </div>
    <section>
      <div class="container box">
        <index-launcher></index-launcher>
      </div>
    </section>
  </article>
</template>

<script>
import VueMarkdown from 'vue-markdown'
import IndexLauncher from '@/components/IndexLauncher'
import Pricer from '../pricer.js'

function updatePriceTable(table)
{
  //Cells are: PageCount, USD, EUR, GBP
  var rows = table.querySelector('tbody').querySelectorAll('tr')
  rows.forEach( r => {
    var cells = r.cells
    if (cells.length >= 4) {
      const pages = parseInt(cells[0].innerText)
      cells[1].innerText = cells[1].innerText.replace(/[0-9\.]+/, (Pricer.calculatePrice('a4',pages,'USD')/100).toFixed(2))
      cells[2].innerText = cells[2].innerText.replace(/[0-9\.]+/, (Pricer.calculatePrice('a4',pages,'EUR')/100).toFixed(2))
      cells[3].innerText = cells[3].innerText.replace(/[0-9\.]+/, (Pricer.calculatePrice('a4',pages,'GBP')/100).toFixed(2))
    }
  })
}

function updateShippingTable(table)
{
  var rows = table.querySelector('tbody').querySelectorAll('tr')
  if (rows.length >= 2) {
    var limits = rows[0].cells[0].innerText
    limits = limits.replace(/USD/, Pricer.freeShippingThreshold('a4','USD')/100)
    limits = limits.replace(/EUR/, Pricer.freeShippingThreshold('a4','EUR')/100)
    limits = limits.replace(/GBP/, Pricer.freeShippingThreshold('a4','GBP')/100)
    rows[0].cells[0].innerText = limits

    //Cells are: , USD, EUR, GBP
    var cells = rows[1].cells
    console.log({cells})
    cells[1].innerText = cells[1].innerText.replace(/[0-9\.]+/, (Pricer.shippingPrice('a4',0,0,'USD')/100).toFixed(2))
    cells[2].innerText = cells[2].innerText.replace(/[0-9\.]+/, (Pricer.shippingPrice('a4',0,0,'EUR')/100).toFixed(2))
    cells[3].innerText = cells[3].innerText.replace(/[0-9\.]+/, (Pricer.shippingPrice('a4',0,0,'GBP')/100).toFixed(2))
  }
}

const SITE = 'https://pixxibook.com'
const ORG_ID = SITE+'/#organization'

//read back the local date: toISOString() would shift a local midnight into
//the previous day for anyone building east of UTC
function isoDate(text) {
  var d = text && new Date(text)
  var pad = function(n) { return (n < 10 ? '0' : '')+n }
  return !d || isNaN(d) ? null
    : d.getFullYear()+'-'+pad(d.getMonth()+1)+'-'+pad(d.getDate())
}

function plainText(md) {
  return md.replace(/<[^>]*>/g, '')
           .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
           .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
           .replace(/[*_`]/g, '')
           .replace(/\s+/g, ' ')
           .trim()
}

export default {
  name: 'DocViewerPage',
  components: {
    VueMarkdown,
    IndexLauncher,
  },
  metaInfo() {
    var info = { title: this.pageTitle, meta: this.ogMeta }
    if (this.schemaGraph) {
      //vue-meta escapes innerHTML unless the sanitiser is turned off
      info.script = [{ vmid: 'ld', type: 'application/ld+json',
                       innerHTML: JSON.stringify(this.schemaGraph) }]
      info.__dangerouslyDisableSanitizers = ['innerHTML']
    }
    return info
  },
  data() { return {
    meta: {},
    dir: '',
    source: '',
    index: [],
    curpath: '',
  }},
  computed: {
    dirName: function() {
      return { journal: 'Pixxi Journal',
               doc: 'Document Index' }[this.dir] || '~~Mysterious Stuff~~'
    },
    pageTitle: function() {
      return this.meta.title || this.dirName
    },
    pageImage: function() {
      return ('https://pixxibook.com'+this.meta.image) || ''
    },
    ogMeta: function() {
      var meta = []
      var isLarge = !!this.meta.image
      if (isLarge) {
        meta.push({ vmid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' })
        meta.push({ vmid: 'og:image', name: 'og:image', content: 'https://pixxibook.com'+this.meta.image })
      } else if (this.meta.thumb) {
        meta.push({ vmid: 'og:image', name: 'og:image', content: 'https://pixxibook.com'+this.meta.thumb })
      }
      if (this.pageTitle) meta.push({ vmid: 'og:title', name: 'og:title', content: this.pageTitle })
      var desc = this.meta.desc || this.meta.summary
      if (desc) {
        desc = desc.replace(/\s+/g, ' ').trim()
        meta.push({ vmid: 'og:description', name: 'og:description', content: desc })
        meta.push({ vmid: 'description', name: 'description', content: desc.slice(0, 200) })
      }
      return meta
    },
    //built from the markdown so the answers are written once: each '## ...?'
    //heading and the prose beneath it becomes a Question
    faqSchema: function() {
      var faqs = []
      var question = null
      var answer = []
      var lines = (this.source || '').split('\n')
      lines.push('#') //terminator, so the last answer is collected
      lines.forEach(line => {
        if (line[0] == '#') {
          var text = plainText(answer.join(' '))
          if (question && text) {
            faqs.push({ '@type': 'Question', name: question,
                        acceptedAnswer: { '@type': 'Answer', text: text } })
          }
          var heading = line.match(/^##\s+(.*\?)\s*$/)
          question = heading ? plainText(heading[1]) : null
          answer = []
        } else if (question) {
          answer.push(line)
        }
      })
      return faqs.length < 2 ? null
        : { '@type': 'FAQPage', mainEntity: faqs }
    },
    breadcrumbSchema: function() {
      if (!this.dir || !this.meta.title) return null //not the index pages
      return { '@type': 'BreadcrumbList', itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'PixxiBook', item: SITE },
        { '@type': 'ListItem', position: 2, name: this.dirName, item: SITE+'/'+this.dir },
        { '@type': 'ListItem', position: 3, name: this.pageTitle },
      ]}
    },
    articleSchema: function() {
      if (this.dir != 'journal' || !this.meta.title) return null
      var article = { '@type': 'BlogPosting',
                      headline: this.meta.title,
                      mainEntityOfPage: SITE+this.$route.path.replace(/\/+$/, ''),
                      publisher: { '@id': ORG_ID } }
      var published = isoDate(this.meta.date)
      if (published) article.datePublished = published
      if (this.meta.author) {
        article.author = { '@type': 'Person',
                           name: this.meta.author.replace(/^./, c => c.toUpperCase()) }
      }
      var image = this.meta.image || this.meta.thumb
      if (image) article.image = SITE+image
      var desc = this.meta.desc || this.meta.summary
      if (desc) article.description = plainText(desc)
      return article
    },
    //the page that carries the price table is the one that describes the book
    productSchema: function() {
      if ((this.source || '').indexOf('id="price-table"') < 0) return null
      var image = this.meta.thumb || this.meta.image
      return { '@type': 'Product',
               name: 'Medium Portrait Hardcover Blog Book',
               description: this.meta.desc || this.meta.summary,
               image: image ? SITE+image : undefined,
               brand: { '@id': ORG_ID },
               offers: Object.keys(Pricer.Currencies).map(currency => ({
                 '@type': 'AggregateOffer',
                 priceCurrency: currency,
                 //a4 pricing is base plus per-page, floored at 24 pages
                 lowPrice: (Pricer.calculatePrice('a4', 0, currency)/100).toFixed(2),
                 highPrice: (Pricer.calculatePrice('a4', 300, currency)/100).toFixed(2),
                 availability: 'https://schema.org/InStock',
                 seller: { '@id': ORG_ID },
               })) }
    },
    //the embed, its caption and the prose introducing it are already written
    //in the markdown; only the date and running time come from the frontmatter
    videoSchema: function() {
      var source = this.source || ''
      var embed = source.match(/youtube\.com\/embed\/([\w-]+)/)
      var uploaded = isoDate(this.meta.videodate)
      if (!embed || !uploaded) return null
      var figure = source.search(/<figure|<iframe/)
      var intro = plainText(source.slice(0, figure > 0 ? figure : embed.index)
                                  .split(/^#.*$/m).pop())
      var caption = source.match(/<figcaption>([^<]*)<\/figcaption>/)
      var video = { '@type': 'VideoObject',
                    name: caption ? plainText(caption[1]) : this.pageTitle,
                    description: intro || this.meta.desc,
                    thumbnailUrl: 'https://i.ytimg.com/vi/'+embed[1]+'/hqdefault.jpg',
                    embedUrl: 'https://www.youtube.com/embed/'+embed[1],
                    contentUrl: 'https://www.youtube.com/watch?v='+embed[1],
                    uploadDate: uploaded,
                    publisher: { '@id': ORG_ID } }
      if (this.meta.videoduration) video.duration = this.meta.videoduration
      return video
    },
    schemaGraph: function() {
      var parts = [this.breadcrumbSchema, this.articleSchema, this.productSchema,
                   this.videoSchema, this.faqSchema].filter(s => s)
      return parts.length ? { '@context': 'https://schema.org', '@graph': parts } : null
    },
  },
  methods: {
    maybeReload: function(path) {
      if (path != this.curpath) {
        this.curpath = path
        this.reload(path)
      } else {
        console.log('drop duplicate reload', path)
      }
    },
    reload: function(path) {
      path = path.replace(/\/$/, '') //remove trailing / (prerendered case)
      var isIndex = path.indexOf('/') == path.lastIndexOf('/')
      console.log('reload',path, isIndex)
      var doc = isIndex ? '/static/content'+path+'/index.json': '/static/content'+path+'.md'
      this.dir = path.split('/')[1]
      this.meta = {}
      this.$http.get(doc).then( (rsp) => {
        if (isIndex) {
          this.index = rsp.body.filter(p => ( !p.tags
                                              || p.tags.indexOf('draft')==-1
                                              || this.$store.state.dev.developmentClient )
                                       && ( !this.$route.query.cat
                                            || p.tags.indexOf(this.$route.query.cat)!=-1) )
          this.source = ''
        } else {
          this.index = []
          this.source = this.extractHeader(rsp.body)
        }
      }).catch( (err) => {
        this.source = '## ERROR\n' + err.statusText
      })
    },
    extractHeader: function(txt) {
      if (txt) {
        txt = txt.replace('\r','\n')
        var lines = txt.split('\n')
        var i = 0;
        if (lines.length > 2 & lines[i].trim()=='---') { //yaml header
          i++;
          while (lines[i].trim() != '---') {
            var f = lines[i].split(':')
            if (f.length > 1) {
              this.$set(this.meta, f[0], f[1].trim())
            }
            i++
          }
          i++
        }
        if (this.meta.tags) {
          this.meta.tags = this.meta.tags.split(',')
        }
        return lines.slice(i).join('\n')
      }
      return txt
    },
    //the doc title in the header is the page's h1, so push body headings
    //down a level: # -> h2, ## -> h3, ... (h6 stays put)
    demoteHeadings: function(html) {
      return html.replace(/<(\/?)h([1-5])\b/g,
                          (m, slash, level) => '<'+slash+'h'+(+level+1))
    },
    postProcess: function() {
      //send relative links via router
      var links = document.querySelectorAll('#doc a')
      if (links) {
        var absRE = new RegExp('http://|https://|mailto:|//', 'i');
        for (var i=0; i<links.length; ++i) {
          var href = links[i].getAttribute('href')
          console.log('check href=',href)
          var router = this.$router
          if (href && !absRE.test(href)) {
            links[i].onclick = function(ev) {
              ev.preventDefault()
              router.push(ev.currentTarget.getAttribute('href'))
            }
          } else {
            links[i].setAttribute('target', links[i].getAttribute('target') || '_blank')
          }
        }
      }
      //move img to figure and use alt text as figcaption
      var imgs = document.querySelectorAll('#doc img')
      if (imgs) {
        imgs.forEach(el => {
          if (el.getAttribute("alt") !== null) {
            const wrapper = document.createElement('figure');
            el.parentNode.insertBefore(wrapper, el);
            el.parentNode.removeChild(el);
            wrapper.appendChild(el);
            if (el.getAttribute("alt")) {
              const caption = document.createElement('figcaption');
              caption.appendChild(document.createTextNode(el.getAttribute("alt")));
              wrapper.appendChild(caption);
            }
            var src = el.getAttribute('src')
            var klasspt = src.search('!')
            if (klasspt >= 0) {
              el.setAttribute('src', src.substr(0,klasspt))
              wrapper.setAttribute('class', src.substr(klasspt+1).replace(',',' '))
            }
          }
        })
      }

      var subels = document.querySelectorAll('.pixxi-subsitute')
      subels.forEach( el => {
        const id = el.getAttribute('id')

        if (id=='price-table') {
          updatePriceTable(el)
        } else if (id=='shipping-table') {
          updateShippingTable(el)
        }
      })
    },
  },
  watch: {
    source: function() {
      this.$nextTick(function() {
        if (this.$route.hash) {
          var anchors = document.getElementsByName(this.$route.hash.slice(1))
          if (anchors) {
            anchors[0].scrollIntoView();
          }
        }
        this.postProcess();
      })
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => vm.maybeReload(to.path))
  },
  beforeRouteUpdate(to, from, next) {
    // re-using component for another doc page
    this.maybeReload(to.path)
    next()
  },
  mounted() {
    //for pre-rendered page
    this.maybeReload(this.$route.path)
  }

}
</script>

<style scoped>
section {
    margin-top: 2em;
    margin-bottom: 2em;
}
.fill-height {
    min-height: 50vh;
}
#summary {
    color: #363636;
    margin: 1ex 2em 1em 2em;
}
.tag.draft {
    color: black;
    background-color: yellow;
}

figure.thumb {
  padding: 1ex;
  background-color: white;
  border: thin silver solid;
  max-width: 10em;
  border: thin solid silver;
  float: left;
  margin: 1ex 2ex 1ex 0;
}

#doc {
  max-width: 120ex;
  margin: auto;
}
#doc header {
  -background-color: #363636;
  margin: 2em 0 2em 0;
  padding: 1ex;
  border-bottom: .5ex solid black;
}

#doc header h1 {
  font-size: 2.5em;
  font-weight: bold;
  color: black;
}
#doc header #meta {
  display: flex;
  justify-content: space-between;
}
#doc header #meta #tags {
  margin-left: auto;
}
#doc #float-clearer { clear: both; }
</style>

<style>
/* markdown body tags are not scoped */
#body figure {
  padding: 1ex;
  margin: auto;
  width: 40rem;
  max-width: 90vw;;
  x-display: block;
}
#body figure:not(.nobox) {
  background-color: white;
  border: thin silver solid;
}
#body figure figcaption {
    padding: 1ex 10%;
    font-size: 1rem;
}
#body figure.medium {
  max-width: 40em;
}
#body figure.small {
    max-width: 20em;
    x-border: none;
}
#body figure.tiny {
    max-width: 10em;
    x-border: none;
}
#body figure.logo {
    max-width: 3em;
    border: none;
}
#body figure.left {
  float: left;
  margin: 1ex 1em 1ex 0;
}
#body figure.right {
  float: right;
  margin: 1ex 0 1ex 1em;
}
#body .clear {
  clear: both;
}

#body h2 { font-size: 1.75em; font-weight: bold; clear: both;
           border-bottom: solid #ff3860; margin-right: 5em; }
#body h3 { font-size: 1.5em; font-weight: bold; clear: both;}
#body h4 { font-size: 1.25em; font-weight: bold; clear: both;}
#body table { width: initial; max-width: 100%; min-width: 50%; }
#body p { font-size: 125%; }
</style>



// WEBPACK FOOTER //
// src/components/DocViewerPage.vue